/**
 * BrandLogos.jsx
 * Simple horizontally scrollable "Shop your Favourite Brands Laptops" section.
 */
import React, { useRef, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTheme } from "../../../contexts/ThemeContext";
import { BrandShimmer } from "../../../components/ShimmerEffect";

/* ── Inline SVG icons for known brands ──────────────────────────────────── */
export const BRAND_ICONS = {
  ASUS: (
    <svg viewBox="0 0 120 40" width="80" height="26" fill="currentColor">
      <text x="0" y="32" fontSize="36" fontWeight="900" fontFamily="Arial, sans-serif" letterSpacing="-1">ASUS</text>
    </svg>
  ),
  ACER: (
    <svg viewBox="0 0 100 40" width="72" height="26">
      <text x="0" y="32" fontSize="36" fontWeight="700" fontFamily="Arial, sans-serif" fill="currentColor">acer</text>
    </svg>
  ),
  APPLE: (
    <svg viewBox="0 0 24 24" width="32" height="32" fill="currentColor">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  ),
  DELL: (
    <svg viewBox="0 0 60 40" width="60" height="26">
      <text x="0" y="32" fontSize="36" fontWeight="900" fontFamily="Arial, sans-serif" fill="currentColor">DELL</text>
    </svg>
  ),
  HP: (
    <svg viewBox="0 0 50 40" width="44" height="30" fill="currentColor">
      <path d="M20 0L0 40h8l4-10h14l-4 10h8L40 0h-8L28 14H14L18 0zm22 0l-4 10h14l4-10z" transform="scale(0.6) translate(4,2)"/>
      <text x="0" y="32" fontSize="36" fontWeight="900" fontFamily="Arial, sans-serif">HP</text>
    </svg>
  ),
  LENOVO: (
    <svg viewBox="0 0 130 40" width="90" height="26">
      <text x="0" y="32" fontSize="36" fontWeight="700" fontFamily="Arial, sans-serif" fill="currentColor">Lenovo</text>
    </svg>
  ),
  MSI: (
    <svg viewBox="0 0 80 40" width="56" height="26">
      <text x="0" y="32" fontSize="36" fontWeight="900" fontFamily="Arial, sans-serif" fill="currentColor">MSI</text>
    </svg>
  ),
  MICROSOFT: (
    <svg viewBox="0 0 24 24" width="32" height="32">
      <rect x="0" y="0" width="11" height="11" fill="#F25022"/>
      <rect x="13" y="0" width="11" height="11" fill="#7FBA00"/>
      <rect x="0" y="13" width="11" height="11" fill="#00A4EF"/>
      <rect x="13" y="13" width="11" height="11" fill="#FFB900"/>
    </svg>
  ),
  RAZER: (
    <svg viewBox="0 0 100 40" width="74" height="26">
      <text x="0" y="32" fontSize="36" fontWeight="900" fontFamily="Arial, sans-serif" fill="#00d819">RAZER</text>
    </svg>
  ),
  SAMSUNG: (
    <svg viewBox="0 0 140 40" width="96" height="26">
      <text x="0" y="32" fontSize="32" fontWeight="700" fontFamily="Arial, sans-serif" fill="currentColor">SAMSUNG</text>
    </svg>
  ),
  GALAXY: (
    <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
      <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm0-14c-3.31 0-6 2.69-6 6s2.69 6 6 6 6-2.69 6-6-2.69-6-6-6zm0 10c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4z"/>
    </svg>
  ),
  ALIENWARE: (
    <svg viewBox="0 0 140 40" width="100" height="26">
      <text x="0" y="32" fontSize="28" fontWeight="900" fontFamily="Arial, sans-serif" fill="currentColor" letterSpacing="1">ALIENWARE</text>
    </svg>
  ),
  GIGABYTE: (
    <svg viewBox="0 0 120 40" width="90" height="26">
      <text x="0" y="32" fontSize="28" fontWeight="800" fontFamily="Arial, sans-serif" fill="currentColor">GIGABYTE</text>
    </svg>
  ),
  AORUS: (
    <svg viewBox="0 0 100 40" width="80" height="26">
      <text x="0" y="32" fontSize="32" fontWeight="900" fontFamily="Arial, sans-serif" fill="#ff6600">AORUS</text>
    </svg>
  ),
  LG: (
    <svg viewBox="0 0 60 40" width="40" height="26">
      <text x="0" y="32" fontSize="36" fontWeight="900" fontFamily="Arial, sans-serif" fill="#c3002f">LG</text>
    </svg>
  ),
  VAIO: (
    <svg viewBox="0 0 80 40" width="60" height="26">
      <text x="0" y="32" fontSize="32" fontWeight="300" fontFamily="Arial, sans-serif" fill="currentColor">VAIO</text>
    </svg>
  ),
  TOSHIBA: (
    <svg viewBox="0 0 120 40" width="90" height="26">
      <text x="0" y="32" fontSize="28" fontWeight="900" fontFamily="Arial, sans-serif" fill="#ff0000">TOSHIBA</text>
    </svg>
  ),
  FUJITSU: (
    <svg viewBox="0 0 120 40" width="90" height="26">
      <text x="0" y="32" fontSize="30" fontWeight="900" fontFamily="Arial, sans-serif" fill="#ff0000">FUJITSU</text>
    </svg>
  ),
  PANASONIC: (
    <svg viewBox="0 0 140 40" width="100" height="26">
      <text x="0" y="32" fontSize="26" fontWeight="700" fontFamily="Arial, sans-serif" fill="#0000ff">Panasonic</text>
    </svg>
  ),
  CHUWI: (
    <svg viewBox="0 0 100 40" width="70" height="26">
      <text x="0" y="32" fontSize="28" fontWeight="800" fontFamily="Arial, sans-serif" fill="#ff6600">CHUWI</text>
    </svg>
  ),
  INFINIX: (
    <svg viewBox="0 0 100 40" width="70" height="26">
      <text x="0" y="32" fontSize="28" fontWeight="800" fontFamily="Arial, sans-serif" fill="#00ff00">Infinix</text>
    </svg>
  ),
  HUAWEI: (
    <svg viewBox="0 0 120 40" width="90" height="26">
      <text x="0" y="32" fontSize="30" fontWeight="900" fontFamily="Arial, sans-serif" fill="#ff0000">HUAWEI</text>
    </svg>
  ),
  HONOR: (
    <svg viewBox="0 0 100 40" width="80" height="26">
      <text x="0" y="32" fontSize="32" fontWeight="900" fontFamily="Arial, sans-serif" fill="currentColor">HONOR</text>
    </svg>
  ),
  XIAOMI: (
    <svg viewBox="0 0 100 40" width="80" height="26">
      <text x="0" y="32" fontSize="30" fontWeight="900" fontFamily="Arial, sans-serif" fill="#ff6700">XIAOMI</text>
    </svg>
  ),
  MI: (
    <svg viewBox="0 0 40 40" width="30" height="30">
      <rect width="40" height="40" rx="8" fill="#ff6700" />
      <text x="8" y="28" fontSize="20" fontWeight="900" fontFamily="Arial, sans-serif" fill="#fff">MI</text>
    </svg>
  ),
  REALME: (
    <svg viewBox="0 0 100 40" width="80" height="26">
      <text x="0" y="32" fontSize="30" fontWeight="800" fontFamily="Arial, sans-serif" fill="#ffc915">realme</text>
    </svg>
  ),
  AVITA: (
    <svg viewBox="0 0 100 40" width="70" height="26">
      <text x="0" y="32" fontSize="32" fontWeight="800" fontFamily="Arial, sans-serif" fill="#4b0082">AVITA</text>
    </svg>
  ),
  COMPAQ: (
    <svg viewBox="0 0 120 40" width="90" height="26">
      <text x="0" y="32" fontSize="32" fontWeight="900" fontFamily="Arial, sans-serif" fill="#ff0000">COMPAQ</text>
    </svg>
  ),
  SONY: (
    <svg viewBox="0 0 100 40" width="80" height="26">
      <text x="0" y="32" fontSize="32" fontWeight="900" fontFamily="Arial, sans-serif" fill="currentColor">SONY</text>
    </svg>
  )
};

const BRAND_COLORS = {
  ASUS:      { from: "#00c4cc", to: "#0073e6" },
  ACER:      { from: "#83b735", to: "#2e7d32" },
  APPLE:     { from: "#636363", to: "#111111" },
  DELL:      { from: "#007db8", to: "#0040a0" },
  HP:        { from: "#0096d6", to: "#003087" },
  LENOVO:    { from: "#e2231a", to: "#8b0000" },
  MSI:       { from: "#ff0000", to: "#8b0000" },
  MICROSOFT: { from: "#F25022", to: "#00A4EF" },
  RAZER:     { from: "#00d819", to: "#008a0e" },
  SAMSUNG:   { from: "#1428a0", to: "#0d47a1" },
  GALAXY:    { from: "#1428a0", to: "#0d47a1" },
  ALIENWARE: { from: "#00e5ff", to: "#0088cc" },
  GIGABYTE:  { from: "#ff6600", to: "#cc3300" },
  AORUS:     { from: "#ff6600", to: "#cc3300" },
  LG:        { from: "#c3002f", to: "#8a0022" },
  VAIO:      { from: "#000000", to: "#444444" },
  TOSHIBA:   { from: "#ff0000", to: "#b30000" },
  FUJITSU:   { from: "#ff0000", to: "#b30000" },
  PANASONIC: { from: "#0000ff", to: "#0000b3" },
  CHUWI:     { from: "#ff6600", to: "#cc3300" },
  INFINIX:   { from: "#00ff00", to: "#00b300" },
  HUAWEI:    { from: "#ff0000", to: "#b30000" },
  HONOR:     { from: "#000000", to: "#444444" },
  XIAOMI:    { from: "#ff6700", to: "#cc5200" },
  MI:        { from: "#ff6700", to: "#cc5200" },
  REALME:    { from: "#ffc915", to: "#cca011" },
  AVITA:     { from: "#4b0082", to: "#2d004e" },
  COMPAQ:    { from: "#ff0000", to: "#b30000" },
  SONY:      { from: "#000000", to: "#444444" }
};

export const getMatchedBrandKey = (name = "") => {
  const upperOriginal = name.trim().toUpperCase();
  const normalizedInput = upperOriginal.replace(/[^A-Z0-9]/g, "");
  
  if (BRAND_ICONS[normalizedInput]) return normalizedInput;

  const aliases = {
    MACBOOK: "APPLE",
    MAC: "APPLE",
    HEWLETTPACKARD: "HP",
    THINKPAD: "LENOVO",
    IDEAPAD: "LENOVO",
    PREDATOR: "ACER",
    ROG: "ASUS",
    ZENBOOK: "ASUS",
    XPS: "DELL",
    INSPIRON: "DELL",
    SURFACE: "MICROSOFT",
  };

  for (const [alias, realKey] of Object.entries(aliases)) {
    if (new RegExp(`\\b${alias}\\b`).test(upperOriginal) || normalizedInput === alias) {
      return realKey;
    }
  }

  const availableKeys = Object.keys(BRAND_ICONS).sort((a, b) => b.length - a.length);
  for (const key of availableKeys) {
    if (new RegExp(`\\b${key}\\b`).test(upperOriginal)) {
      return key;
    }
  }

  return upperOriginal;
};

const getIcon = (name) => BRAND_ICONS[getMatchedBrandKey(name)] || null;
const getColors = (name) =>
  BRAND_COLORS[getMatchedBrandKey(name)] || { from: "#6366f1", to: "#8b5cf6" };

const InitialsAvatar = ({ name, colors }) => {
  const initials = name.slice(0, 2).toUpperCase();
  return (
    <div
      style={{
        width: 44, height: 44, borderRadius: "50%",
        background: `linear-gradient(135deg, ${colors.from}, ${colors.to})`,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 16, fontWeight: 700, color: "#fff",
      }}
    >
      {initials}
    </div>
  );
};

/* ── Single brand card ───────────────────────────────────────────────────── */
const BrandCard = ({ brand, onClick, isDark }) => {
  const icon = getIcon(brand.name);
  const colors = getColors(brand.name);
  const hasLogo = !!brand.logo;

  return (
    <div
      className="flex flex-col items-center justify-center min-w-[140px] max-w-[140px] h-[130px] rounded-xl bg-white dark:bg-[#1a1a24] border border-gray-100 dark:border-gray-800 shadow-sm hover:shadow-md transition-all cursor-pointer p-4 shrink-0 hover:-translate-y-1"
      onClick={onClick}
      title={`Shop ${brand.name} Laptops`}
    >
      <div className="flex-1 flex items-center justify-center w-full mb-3">
        {hasLogo ? (
          <>
            <img
              src={brand.logo}
              alt={brand.name}
              className={`max-w-[80px] max-h-[45px] object-contain transition-all duration-300 ${
                isDark
                  ? "[filter:brightness(0)_invert(1)_opacity(0.8)] hover:[filter:brightness(1)_opacity(1)]"
                  : "[filter:brightness(0)_opacity(0.8)] hover:[filter:brightness(0)_opacity(1)]"
              }`}
              onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }}
            />
            <div style={{ display: "none" }}>
              {icon || <InitialsAvatar name={brand.name} colors={colors} />}
            </div>
          </>
        ) : icon ? (
          <div className="flex items-center justify-center opacity-80 hover:opacity-100 transition-all duration-300" style={{ color: colors.from }}>
            {icon}
          </div>
        ) : (
          <InitialsAvatar name={brand.name} colors={colors} />
        )}
      </div>
      <span className="text-[14px] font-semibold text-gray-800 dark:text-gray-200 text-center truncate w-full">
        {brand.name}
      </span>
    </div>
  );
};

/* ── Main section ────────────────────────────────────────────────────────── */
const BrandLogos = ({ brands = [], loading = false }) => {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    if (scrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(Math.ceil(scrollLeft + clientWidth) < scrollWidth);
    }
  };

  useEffect(() => {
    checkScroll();
    window.addEventListener("resize", checkScroll);
    return () => window.removeEventListener("resize", checkScroll);
  }, [brands]);

  const scrollContainer = (direction) => {
    if (scrollRef.current) {
      const { scrollLeft, clientWidth } = scrollRef.current;
      const scrollTo = direction === "left" ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      scrollRef.current.scrollTo({ left: scrollTo, behavior: "smooth" });
    }
  };

  return (
    <section className="py-2 bg-gray-50/50 dark:bg-gray-900/50">
      <div className="container mx-auto px-4">
        <div className="mb-1 flex items-center flex-wrap gap-2">
          {/* <div className="inline-flex items-center gap-[6px] bg-yellow-500/10 border border-yellow-500/25 rounded-full px-3 py-0.5 text-[10px] font-bold tracking-[0.14em] uppercase text-yellow-500 ml-1">
              <span className="w-1.5 h-1.5 rounded-full bg-yellow-500 inline-block" />
              BRANDS
            </div> */}
          <h2 className="text-[clamp(1.2rem,2.5vw,1.6rem)] font-black text-gray-900 dark:text-white leading-tight flex items-center flex-wrap gap-x-2 gap-y-1">
            <span>Shop your</span>
            <span className="bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 bg-clip-text text-transparent">
              Favourite Brands
            </span>
            <span>Laptops</span>
          </h2>
        </div>

        {loading ? (
          <BrandShimmer dark={isDark} />
        ) : brands.length === 0 ? (
          <div className="text-gray-500 dark:text-[#444] py-4">
            No brands available yet.
          </div>
        ) : (
          <div className="relative group">
            <button
              onClick={() => scrollContainer("left")}
              className={`absolute left-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${canScrollLeft ? "opacity-0 group-hover:opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"}`}
              aria-label="Scroll left"
            >
              <ChevronLeft size={22} />
            </button>

            <div
              ref={scrollRef}
              onScroll={checkScroll}
              className="flex overflow-x-auto pb-1 gap-4 snap-x scrollbar-hide scroll-smooth"
            >
              {brands.map((brand) => (
                <div key={brand.id} className="snap-center">
                  <BrandCard
                    brand={brand}
                    isDark={isDark}
                    onClick={() => navigate(`/all-products?brand=${encodeURIComponent(brand.name)}`)}
                  />
                </div>
              ))}
            </div>

            <button
              onClick={() => scrollContainer("right")}
              className={`absolute right-[-20px] top-[45%] -translate-y-1/2 z-10 hidden md:flex items-center justify-center w-10 h-10 rounded-full bg-white dark:bg-gray-800 shadow-md border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 transition-all hover:scale-105 ${canScrollRight ? "opacity-0 group-hover:opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"}`}
              aria-label="Scroll right"
            >
              <ChevronRight size={22} />
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default BrandLogos;
